import type { FilterState, DevelopmentContentDuration, DevelopmentContentLastUpdated } from '../../types';
import type { OptionGroup } from '~/features/common/components/FilterMultiSelect';
import { Button, Col, Row } from '@workramp/ui';
import { useMemo } from 'react';
import { isEmpty } from 'underscore';
import { MixPanel } from '~/common';
import { FilterMultiSelect } from '~/features/common/components/FilterMultiSelect';
import { DevelopmentContentSearchLastUpdatedFilter } from '../DevelopmentContentSearchLastUpdatedFilter';
import { useGetDevelopmentContentFilters } from '../../api';
import { DURATION_FILTER_ID_TO_NAME, LAST_UPDATED_FILTER_ID_TO_NAME } from '../../constants';
import { initialState } from '../../hooks';

interface DevelopmentContentFilterProps {
  selectedState: FilterState;
  onChangeFilter: (filterState: FilterState) => void;
}

const { I18n, APP_CONSTANTS } = window;

const TOPICS = I18n.t('js.learner.development_content.filters.topics');
const DURATION = I18n.t('js.learner.development_content.filters.duration');
const LANGUAGE = I18n.t('js.learner.development_content.filters.language');
const PROVIDER = I18n.t('js.learner.development_content.filters.provider');
const MIXPANEL_EVENT_FILTER_TOPIC = 'go1_learner_view_search_filter_topic';
const MIXPANEL_EVENT_FILTER_LANG = 'go1_learner_view_search_filter_language';
const MIXPANEL_EVENT_FILTER_DURATION = 'go1_learner_view_search_filter_duration';
const MIXPANEL_EVENT_FILTER_LAST_UPDATED = 'go1_learner_view_search_filter_last_updated';
const MIXPANEL_EVENT_FILTER_PROVIDER = 'go1_learner_view_search_filter_provider';
const MIXPANEL_EVENT_RESET = 'go1_learner_view_search_reset_button';

export const DevelopmentContentFilter = ({ onChangeFilter, selectedState }: DevelopmentContentFilterProps) => {
  const getContentFiltersApiResult = useGetDevelopmentContentFilters();

  let durationFilterOptions: OptionGroup[] = [{ id: 'duration', items: [] }];
  let topicFilterOptions: OptionGroup[] = [{ id: 'topic', items: [] }];
  let languageFilterOptions: OptionGroup[] = [{ id: 'language', items: [] }];
  let providerFilterOptions: OptionGroup[] = [{ id: 'provider', items: [] }];
  let lastUpdatedFilterOptions: OptionGroup[] = [{ id: 'last-updated', items: [] }];

  const isAnyNonSearchFilterActive = useMemo(
    () =>
      selectedState.duration.length > 0 ||
      selectedState.topic.length > 0 ||
      selectedState.provider.length > 0 ||
      selectedState.language.length > 0 ||
      selectedState.lastUpdated !== APP_CONSTANTS.GO1_SEARCH_LAST_UPDATED_OPTION.ALL_TIME,
    [
      selectedState.duration,
      selectedState.language,
      selectedState.lastUpdated,
      selectedState.provider,
      selectedState.topic,
    ],
  );

  if (getContentFiltersApiResult.isError) {
    // return empty html if there is an error
    return <> </>;
  }

  if (!getContentFiltersApiResult.isLoading) {
    // Build out the Duration Filter Select Dropdown options
    durationFilterOptions = [
      {
        id: 'duration',
        items: getContentFiltersApiResult.data?.durations?.map((d) => ({
          label: DURATION_FILTER_ID_TO_NAME[d.name] || d.name,
          value: d.id,
        })),
      },
    ];

    lastUpdatedFilterOptions = [
      {
        id: 'last-updated',
        items: getContentFiltersApiResult.data?.lastUpdated?.map((d) => ({
          label: LAST_UPDATED_FILTER_ID_TO_NAME[d.name] || d.name,
          value: d.id,
        })),
      },
    ];

    // Build out the Topic Filter Select Dropdown options
    topicFilterOptions = [
      {
        id: 'topic',
        items: getContentFiltersApiResult.data?.topics?.map((filterItem) => ({
          label: filterItem.id,
          suffix: `${filterItem.count}`,
          value: filterItem.id,
        })),
      },
    ];

    // Build out the Language Filter Dropdown options
    languageFilterOptions = [
      {
        id: 'language',
        items: getContentFiltersApiResult.data?.languages?.map((filterItem) => ({
          label: new Intl.DisplayNames(['en'], {
            type: 'language',
          }).of(filterItem.id),
          suffix: `${filterItem.count}`,
          value: filterItem.id,
        })),
      },
    ];

    // Build out the Provider Filter Dropdown options
    providerFilterOptions = [
      {
        id: 'provider',
        items: getContentFiltersApiResult.data?.providers?.map((filterItem) => ({
          label: filterItem.name,
          suffix: `${filterItem.count}`,
          value: `${filterItem.id}`,
        })),
      },
    ];
  }

  const onReset = () => {
    MixPanel.track(MIXPANEL_EVENT_RESET);
    if (isEmpty(selectedState.searchKeyword)) {
      onChangeFilter(initialState);
    } else {
      onChangeFilter({ ...initialState, searchKeyword: selectedState.searchKeyword });
    }
  };

  const mixPanelTracking = (key: string, value: any) => {
    if (value && value.length) {
      MixPanel.track(key, { value });
    }
  };

  const handleOnChangeTopic = (value: string[]) => {
    mixPanelTracking(MIXPANEL_EVENT_FILTER_TOPIC, value);
    onChangeFilter({ ...selectedState, topic: value });
  };

  const handleOnChangeProvider = (value: string[]) => {
    mixPanelTracking(MIXPANEL_EVENT_FILTER_PROVIDER, value);
    onChangeFilter({ ...selectedState, provider: value });
  };

  const handleOnChangeLanguage = (value: string[]) => {
    mixPanelTracking(MIXPANEL_EVENT_FILTER_LANG, value);
    onChangeFilter({ ...selectedState, language: value });
  };

  const handleOnChangeDuration = (value: DevelopmentContentDuration[]) => {
    mixPanelTracking(MIXPANEL_EVENT_FILTER_DURATION, value);
    onChangeFilter({ ...selectedState, duration: value });
  };

  const handleOnChangeLastUpdated = (value: DevelopmentContentLastUpdated) => {
    MixPanel.track(MIXPANEL_EVENT_FILTER_LAST_UPDATED, { value });
    onChangeFilter({ ...selectedState, lastUpdated: value });
  };

  return (
    <Row gapX={2} gapY={2} align="center">
      <Col xs="auto">
        <FilterMultiSelect
          label={TOPICS}
          optionsData={topicFilterOptions}
          value={selectedState.topic}
          onChange={handleOnChangeTopic}
          onClear={() => {
            onChangeFilter({ ...selectedState, topic: [] });
          }}
          enableSearch
        />
      </Col>
      <Col xs="auto">
        <FilterMultiSelect
          label={DURATION}
          optionsData={durationFilterOptions}
          value={selectedState.duration}
          onChange={handleOnChangeDuration}
          onClear={() => {
            onChangeFilter({ ...selectedState, duration: [] });
          }}
        />
      </Col>
      <Col xs="auto">
        <FilterMultiSelect
          label={LANGUAGE}
          optionsData={languageFilterOptions}
          value={selectedState.language}
          onChange={handleOnChangeLanguage}
          enableSearch
          onClear={() => {
            onChangeFilter({ ...selectedState, language: [] });
          }}
        />
      </Col>
      <Col xs="auto">
        <FilterMultiSelect
          label={PROVIDER}
          optionsData={providerFilterOptions}
          value={selectedState.provider}
          onChange={handleOnChangeProvider}
          enableSearch
          onClear={() => {
            onChangeFilter({ ...selectedState, provider: [] });
          }}
        />
      </Col>
      <Col xs="auto">
        <DevelopmentContentSearchLastUpdatedFilter
          selection={selectedState.lastUpdated}
          onSelectionChange={handleOnChangeLastUpdated}
          options={lastUpdatedFilterOptions}
        />
      </Col>
      {isAnyNonSearchFilterActive && (
        <Col xs="auto">
          <Button onPress={onReset} variant="text">
            {I18n.t(`js.learner.development_content.search_page.reset`)}
          </Button>
        </Col>
      )}
    </Row>
  );
};
